import { useState, useMemo } from 'react';
import {
  Monitor,
  Tablet,
  Smartphone,
  ShieldCheck,
  CheckCircle2,
  Lock,
  Layers,
  Sparkles,
  Search,
} from 'lucide-react';
import { getContrastColor, formatEuro } from '../../lib/utils.js';
import { HeaderSlot } from '../../registry/slots/HeaderSlot.jsx';
import { HeroSlot } from '../../registry/slots/HeroSlot.jsx';
import { ProductCardSlot } from '../../registry/slots/ProductCardSlot.jsx';
import { FilterSlot } from '../../registry/slots/FilterSlot.jsx';
import { SHOP_CATEGORIES } from '../../lib/shop-taxonomy.js';
import { calculatePriceWithMarkup } from './admin-presets.js';

const SAMPLE_PREVIEW_PRODUCTS = [
  {
    id: 'prod_preview_1060883',
    sku: '1060883-DEMO',
    name: 'OPTEX BXS-RAM Funk-Bewegungsmelder (Demo-Vorschau)',
    slug: 'optex-bxs-ram-demo',
    manufacturer: 'OPTEX (Demo)',
    categoryId: 'cat_alarm',
    categoryName: 'Gefahrenmeldetechnik',
    categorySlug: 'alarmtechnik',
    subcategorySlug: 'bewegungsmelder',
    unit: 'Stück',
    basePriceCents: 24900,
    msrpCents: 29900,
    shortDescription: 'Musterartikel zur Layout-Vorschau (kein Live-Bestand)',
    specs: {
      'Vorschau-Modus': 'Demo-Muster',
      'Erfassungswinkel': '180°',
      'Schutzart': 'IP55',
    },
    images: [{ url: '/assets/images/optex-bxs.jpg', alt: 'OPTEX BXS-RAM (Demo)' }],
    isFeatured: true,
  },
  {
    id: 'prod_preview_7500311',
    sku: '7500311-DEMO',
    name: 'Pulsar EN54C-10A28 LED Netzteil 27.6V (Demo-Vorschau)',
    slug: 'pulsar-en54c-demo',
    manufacturer: 'Pulsar (Demo)',
    categoryId: 'cat_fire',
    categoryName: 'Brandschutz & BMA',
    categorySlug: 'brandschutz',
    subcategorySlug: 'energieversorgung',
    unit: 'Stück',
    basePriceCents: 42450,
    msrpCents: 48900,
    shortDescription: 'Musterartikel zur Layout-Vorschau (kein Live-Bestand)',
    specs: {
      'Vorschau-Modus': 'Demo-Muster',
      'Ausgangsspannung': '27.6 V DC',
    },
    images: [{ url: '/assets/images/pulsar-en54.jpg', alt: 'Pulsar EN54C (Demo)' }],
    isFeatured: false,
  },
];

export function LiveShopMockup({
  tenant,
  draftTheme = {},
}) {
  const [viewportMode, setViewportMode] = useState('desktop'); // 'desktop' | 'tablet' | 'mobile'
  const [activeCategory, setActiveCategory] = useState('alarmtechnik');
  const [activeSubcategory, setActiveSubcategory] = useState('bewegungsmelder');
  const [searchQuery, setSearchQuery] = useState('');

  const primary = draftTheme.primaryColor || tenant?.primaryColor || '#0056b3';
  const secondary = draftTheme.secondaryColor || tenant?.secondaryColor || '#5f5e5f';
  const accent = draftTheme.accentColor || tenant?.accentColor || '#ff9900';
  const radius = draftTheme.borderRadius || '0.375rem';
  const fontFamily = draftTheme.fontFamily || 'Inter, sans-serif';

  const layoutHeader = draftTheme.layout_header || draftTheme.layoutHeader || tenant?.settings?.layout_header || 'slim';
  const layoutHero = draftTheme.layout_hero || draftTheme.layoutHero || tenant?.settings?.layout_hero || 'portal_search';
  const productCardVariant = draftTheme.product_card_variant || draftTheme.productCardVariant || tenant?.settings?.product_card_variant || 'specs_heavy';
  const filterLayout = draftTheme.filter_layout || draftTheme.filterLayout || tenant?.settings?.filter_layout || 'sidebar';

  const rawMarkup = draftTheme.pricingMarkupPercent !== undefined
    ? draftTheme.pricingMarkupPercent
    : (tenant?.settings?.pricing_markup_percent?.percent || (tenant?.settings?.pricing_markup_percent?.markup ? tenant.settings.pricing_markup_percent.markup * 100 : 15));
  const markupPercent = parseFloat(rawMarkup) || 0;

  const primaryFg = getContrastColor(primary);
  const secondaryFg = getContrastColor(secondary);

  const containerMaxWidth =
    viewportMode === 'mobile' ? '375px' : viewportMode === 'tablet' ? '768px' : '100%';

  // Synthesize preview tenant object
  const previewTenant = useMemo(() => {
    return {
      id: tenant?.id || 'ten_preview',
      name: tenant?.name || 'Partner-Shop GmbH',
      slug: tenant?.slug || 'partner-shop',
      domain: tenant?.domain || 'preview.partner-shop.de',
      primaryColor: primary,
      secondaryColor: secondary,
      accentColor: accent,
      logoUrl: draftTheme.logoUrl || tenant?.logoUrl || '',
      phone: tenant?.phone || '',
      contactEmail: tenant?.contactEmail || '',
      settings: {
        ...(tenant?.settings || {}),
        theme_radius: radius,
        layout_header: layoutHeader,
        layout_hero: layoutHero,
        product_card_variant: productCardVariant,
        filter_layout: filterLayout,
        pricing_markup_percent: { markup: markupPercent / 100, percent: markupPercent },
        branding: {
          slogan: draftTheme.slogan || tenant?.settings?.branding?.slogan || 'Professionelle Sicherheitstechnik & B2B Direktbezug',
          accentColor: accent,
        },
      },
    };
  }, [tenant, draftTheme, primary, secondary, accent, radius, layoutHeader, layoutHero, productCardVariant, filterLayout, markupPercent]);

  // Compute products with real-time markup applied
  const dynamicProducts = useMemo(() => {
    return SAMPLE_PREVIEW_PRODUCTS.map((p) => {
      const calculatedCents = calculatePriceWithMarkup(p.basePriceCents, markupPercent);
      return {
        ...p,
        price: {
          cents: calculatedCents,
          currency: 'EUR',
          formatted: formatEuro(calculatedCents),
        },
        msrp: p.msrpCents
          ? {
              cents: p.msrpCents,
              currency: 'EUR',
              formatted: formatEuro(p.msrpCents),
            }
          : null,
      };
    });
  }, [markupPercent]);

  return (
    <div className="space-y-3">
      {/* Top Viewport & Isolation Bar */}
      <div className="flex flex-col gap-2 rounded-lg bg-slate-100 p-2 border border-slate-200 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-slate-800 ml-1">Live Shop-Vorschau</span>
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800 border border-amber-300">
            <ShieldCheck className="h-3 w-3 text-amber-600" />
            Vorschau-Modus (Demo-Muster)
          </span>
        </div>

        {/* Viewport Switcher */}
        <div className="flex items-center gap-1 self-end sm:self-auto">
          <button
            type="button"
            onClick={() => setViewportMode('desktop')}
            className={`flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-all ${
              viewportMode === 'desktop'
                ? 'bg-white text-slate-900 shadow-xs'
                : 'text-slate-600 hover:text-slate-900'
            }`}
            title="Desktop Ansicht (100%)"
          >
            <Monitor className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Desktop</span>
          </button>

          <button
            type="button"
            onClick={() => setViewportMode('tablet')}
            className={`flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-all ${
              viewportMode === 'tablet'
                ? 'bg-white text-slate-900 shadow-xs'
                : 'text-slate-600 hover:text-slate-900'
            }`}
            title="Tablet Ansicht (768px)"
          >
            <Tablet className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Tablet</span>
          </button>

          <button
            type="button"
            onClick={() => setViewportMode('mobile')}
            className={`flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-all ${
              viewportMode === 'mobile'
                ? 'bg-white text-slate-900 shadow-xs'
                : 'text-slate-600 hover:text-slate-900'
            }`}
            title="Mobile Ansicht (375px)"
          >
            <Smartphone className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Mobile</span>
          </button>
        </div>
      </div>

      {/* Scoped Dynamic Shop Preview Container */}
      <div className="flex justify-center overflow-hidden rounded-xl border border-slate-300 bg-slate-200/80 p-3 sm:p-4 transition-all duration-300">
        <div
          id="mockup-preview-container"
          className="w-full overflow-hidden rounded-lg bg-white shadow-xl transition-all duration-300 flex flex-col"
          style={{
            maxWidth: containerMaxWidth,
            fontFamily,
            '--primary': primary,
            '--primary-foreground': primaryFg,
            '--on-primary': primaryFg,
            '--secondary': secondary,
            '--secondary-foreground': secondaryFg,
            '--accent': accent,
            '--radius': radius,
            '--astryx-primary': primary,
            '--astryx-secondary': secondary,
            '--astryx-accent': accent,
            '--astryx-radius': radius,
            '--mockup-primary': primary,
            '--mockup-secondary': secondary,
            '--mockup-accent': accent,
            '--mockup-radius': radius,
            '--mockup-primary-fg': primaryFg,
            '--mockup-secondary-fg': secondaryFg,
          }}
        >
          {/* 1. Dynamic Astryx Header Slot */}
          <div className="border-b border-slate-200">
            <HeaderSlot
              variant={layoutHeader}
              tenant={previewTenant}
              cartCount={2}
              currentPath="/"
            />
          </div>

          {/* 2. Dynamic Astryx Hero Slot */}
          <div className="border-b border-slate-200">
            <HeroSlot
              variant={layoutHero}
              tenant={previewTenant}
              categories={SHOP_CATEGORIES}
              catalogCount={tenant?.metrics?.catalogCount || null}
              featuredProduct={dynamicProducts[0]}
            />
          </div>

          {/* 3. Dynamic Catalog Section with FilterSlot and ProductCardSlot */}
          <div className="flex-1 bg-slate-50/50 p-4">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-slate-900">
                  B2B Sortiment & Fachkatalog
                </h3>
                <p className="text-[11px] text-slate-500">
                  Slot-Vorschau mit angewendetem Margenaufschlag (+{markupPercent.toFixed(1)}%)
                </p>
              </div>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-mono text-slate-600 border border-slate-200">
                Card-Slot: {productCardVariant}
              </span>
            </div>

            {filterLayout === 'topbar' ? (
              /* Topbar Filter Layout */
              <div className="space-y-4">
                <FilterSlot
                  variant="topbar"
                  tenant={previewTenant}
                  categories={SHOP_CATEGORIES}
                  activeCategory={activeCategory}
                  activeSubcategory={activeSubcategory}
                  onSelectCategory={setActiveCategory}
                  onSelectSubcategory={setActiveSubcategory}
                  search={searchQuery}
                  onSearchChange={setSearchQuery}
                  manufacturers={['OPTEX', 'Pulsar', 'SATEL', 'Dahua']}
                />

                <div className="grid gap-3 sm:grid-cols-2">
                  {dynamicProducts.map((prod) => (
                    <ProductCardSlot
                      key={prod.id}
                      variant={productCardVariant}
                      tenant={previewTenant}
                      product={prod}
                    />
                  ))}
                </div>
              </div>
            ) : (
              /* Sidebar Filter Layout */
              <div className="grid gap-4 lg:grid-cols-12">
                <div className="lg:col-span-4">
                  <FilterSlot
                    variant="sidebar"
                    tenant={previewTenant}
                    categories={SHOP_CATEGORIES}
                    activeCategory={activeCategory}
                    activeSubcategory={activeSubcategory}
                    onSelectCategory={setActiveCategory}
                    onSelectSubcategory={setActiveSubcategory}
                    search={searchQuery}
                    onSearchChange={setSearchQuery}
                    manufacturers={['OPTEX', 'Pulsar', 'SATEL', 'Dahua']}
                  />
                </div>
                <div className="lg:col-span-8">
                  <div className="grid gap-3 sm:grid-cols-2">
                    {dynamicProducts.map((prod) => (
                      <ProductCardSlot
                        key={prod.id}
                        variant={productCardVariant}
                        tenant={previewTenant}
                        product={prod}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 4. Mockup Storefront Footer */}
          <footer
            className="border-t px-4 py-3 text-xs transition-colors"
            style={{
              backgroundColor: secondary,
              color: secondaryFg,
            }}
          >
            <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
              <div>
                <span className="font-bold">{previewTenant.name}</span>
                <span className="ml-2 opacity-80 text-[11px]">{previewTenant.phone}</span>
              </div>
              <div className="flex items-center gap-3 text-[11px] opacity-90">
                <span>Impressum</span>
                <span>Datenschutz</span>
                <span>AGB</span>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
}
